import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrainingGuide() {
  const navigate = useNavigate();
  const downloadUrl = `${BACKEND_URL}/api/training-guide.txt`;

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Hidden when printing */}
      <div className="print:hidden bg-card border-b border-border p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/documents')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-primary">Vyrus Hub Training Guide</h1>
        </div>
        <div className="flex flex-col gap-2">
          <a 
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
          >
            <FileDown className="h-4 w-4 mr-2" />
            Download .txt File
          </a>
          <p className="text-xs text-muted-foreground text-center">
            <a href={downloadUrl} className="text-primary underline">Right-click → Save Link As</a>
          </p>
        </div>
      </div>

      {/* Printable Content */}
      <div id="training-content" className="max-w-4xl mx-auto p-8 text-black">
        
        {/* ==================== COVER PAGE ==================== */}
        <div className="text-center mb-16 pb-12 border-b-4 border-pink-500 page-break-after">
          <h1 className="text-6xl font-bold text-pink-500 mb-6">VYRUS HUB</h1>
          <h2 className="text-3xl text-gray-700 mb-4">Complete Training Guide</h2>
          <p className="text-xl text-gray-500 mb-8">The Convention Operating System</p>
          <div className="bg-pink-50 p-6 rounded-lg inline-block">
            <p className="text-gray-600">For Management Team Use</p>
            <p className="text-sm text-gray-500 mt-2">Renee • Biz • Ozzy</p>
          </div>
        </div>

        {/* ==================== OVERVIEW ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            📋 Overview: What is Vyrus Hub?
          </h2>
          
          <p className="text-gray-700 mb-6 text-lg leading-relaxed">
            Vyrus Hub is your all-in-one convention management platform designed specifically for the Vyrus team. 
            It helps you track events, manage commissions, coordinate staff, assign tasks, and analyze your business performance—all in one place.
          </p>

          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <h3 className="font-bold text-xl text-gray-800 mb-4">🎯 Key Capabilities</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded border-l-4 border-pink-500">
                <h4 className="font-bold text-pink-600">Event Management</h4>
                <p className="text-sm text-gray-600">Track conventions, applications, logistics, and financials</p>
              </div>
              <div className="bg-white p-4 rounded border-l-4 border-blue-500">
                <h4 className="font-bold text-blue-600">Commission Tracking</h4>
                <p className="text-sm text-gray-600">Handle art requests from inquiry to completion</p>
              </div>
              <div className="bg-white p-4 rounded border-l-4 border-green-500">
                <h4 className="font-bold text-green-600">Staff Coordination</h4>
                <p className="text-sm text-gray-600">Recruit and manage event staff with shareable forms</p>
              </div>
              <div className="bg-white p-4 rounded border-l-4 border-purple-500">
                <h4 className="font-bold text-purple-600">Business Analytics</h4>
                <p className="text-sm text-gray-600">Track P&L, compare events, optimize strategy</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-6 rounded-lg">
            <h3 className="font-bold text-yellow-700 mb-2">⚠️ User Roles</h3>
            <ul className="space-y-2 text-gray-700">
              <li><strong className="text-pink-600">Management:</strong> Full access to all features including financial data, user management, and analytics</li>
              <li><strong className="text-blue-600">Staff/Employee:</strong> Limited access—can view events and calendar but cannot see financial information</li>
            </ul>
          </div>
        </section>

        {/* ==================== NAVIGATION ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            🧭 Navigation: Sidebar Menu
          </h2>
          
          <p className="text-gray-700 mb-6">
            The left sidebar contains all main navigation tabs. Here's what each one does:
          </p>

          <div className="space-y-3">
            {[
              { name: 'Dashboard', desc: 'Home screen with key metrics and alerts', icon: '🏠' },
              { name: 'Events', desc: 'List of all conventions you\'re tracking', icon: '📍' },
              { name: 'Commissions', desc: 'Customer art requests and orders', icon: '🎨' },
              { name: 'Invoices', desc: 'Track and manage commission invoices', icon: '🧾' },
              { name: 'Convention Sales', desc: 'Create in-person orders that sync to Wix', icon: '🛒' },
              { name: 'Staff Applicants', desc: 'Review applications for event staff', icon: '👥' },
              { name: 'Task Manager', desc: 'Assign and track team tasks', icon: '✅' },
              { name: 'Calendar', desc: 'Visual calendar of all events', icon: '📅' },
              { name: 'Analytics', desc: 'Financial overview and charts', icon: '📊' },
              { name: 'Compare', desc: 'Side-by-side event comparison', icon: '⚖️' },
              { name: 'Optimizer', desc: 'AI recommendations for better ROI', icon: '⚡' },
              { name: 'Reports', desc: 'Generate financial summaries', icon: '📈' },
              { name: 'Documents', desc: 'Store and organize files', icon: '📁' },
              { name: 'Users', desc: 'Manage team member accounts', icon: '👤' },
              { name: 'Find Conventions', desc: 'Database of conventions to apply to', icon: '🔍' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded">
                <span className="text-2xl">{item.icon}</span>
                <div>
                  <span className="font-bold text-gray-800">{item.name}</span>
                  <span className="text-gray-500 ml-2">— {item.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ==================== DASHBOARD ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            🏠 Dashboard Tab
          </h2>

          <p className="text-gray-700 mb-6">
            The Dashboard is your home base. It shows key business metrics at a glance and alerts you to items needing attention.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📊 Metrics Cards</h3>
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <ul className="space-y-2 text-gray-700">
              <li><strong>Total Events:</strong> Number of conventions in your system</li>
              <li><strong>Upcoming Events:</strong> Events scheduled in the future</li>
              <li><strong>Total Revenue:</strong> Sum of all income across all events</li>
              <li><strong>Net Profit:</strong> Total revenue minus total expenses</li>
            </ul>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">🔔 Alert Badges</h3>
          <div className="bg-red-50 p-4 rounded-lg mb-6">
            <p className="text-gray-700 mb-2">Watch for colored badges indicating items that need your attention:</p>
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Pending Approvals:</strong> New employee accounts waiting for approval</li>
              <li>• <strong>Overdue Tasks:</strong> Tasks past their deadline</li>
              <li>• <strong>Pending Commissions:</strong> New commission requests to review</li>
              <li>• <strong>New Messages:</strong> Customer replies on commissions</li>
            </ul>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📋 How To Use Dashboard</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 bg-blue-50 p-4 rounded-lg">
            <li>Check the Dashboard first thing each day</li>
            <li>Click on any alert badge to go directly to that section</li>
            <li>Review the "Upcoming Events" list for what's coming soon</li>
            <li>Monitor net profit to track business health</li>
          </ol>
        </section>

        {/* ==================== EVENTS ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            📍 Events Tab
          </h2>

          <p className="text-gray-700 mb-6">
            This is where you manage all your convention events. Each event tracks everything from application status to financial performance.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">➕ Creating a New Event</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 bg-green-50 p-4 rounded-lg mb-6">
            <li>Click the <strong>"+ Add Event"</strong> button in the top right</li>
            <li>Fill in the event name and location</li>
            <li>Set the start and end dates</li>
            <li>Add setup days if you need extra time before the event</li>
            <li>Set the application status (Not Applied, Applied, Approved, etc.)</li>
            <li>Enter booth cost and projected revenue if known</li>
            <li>Click <strong>"Create Event"</strong> to save</li>
          </ol>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📝 Event Detail Page Features</h3>
          <p className="text-gray-700 mb-4">Click on any event to open its detail page. You'll find these tabs:</p>
          
          <div className="space-y-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-bold text-pink-600 mb-2">📋 Details Tab</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Edit event name, dates, location</li>
                <li>• Update application status</li>
                <li>• Add travel dates and load-in/out times</li>
                <li>• Note trailer info and power needs</li>
                <li>• List staff members working the event</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-bold text-green-600 mb-2">💰 Financials Tab</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• <strong>Add Revenue:</strong> Log daily sales or income</li>
                <li>• <strong>Add Expense:</strong> Track costs by category (travel, supplies, food, etc.)</li>
                <li>• View running P&L for this specific event</li>
                <li>• See ROI percentage automatically calculated</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-bold text-blue-600 mb-2">📸 Photos Tab</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Upload booth setup photos</li>
                <li>• Add captions and categorize photos</li>
                <li>• Reference for future booth layouts</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-bold text-purple-600 mb-2">📝 Debrief Tab</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Record what went well</li>
                <li>• Note what needs improvement</li>
                <li>• Rate crowd traffic and vendor support</li>
                <li>• Mark whether you'd return to this event</li>
                <li>• Capture lessons learned for future reference</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-bold text-orange-600 mb-2">📄 Documents Tab</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Upload event-specific documents</li>
                <li>• Store contracts, floor plans, vendor info</li>
                <li>• Keep everything organized per event</li>
              </ul>
            </div>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">🔗 Staff Application Links</h3>
          <div className="bg-yellow-50 p-4 rounded-lg mb-6">
            <p className="text-gray-700 mb-2">Generate shareable links for people to apply to work your event:</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-700">
              <li>On the Events list, find your event</li>
              <li>Click <strong>"Copy Volunteer Link"</strong> or <strong>"Copy Paid Link"</strong></li>
              <li>Share the link on social media or send directly to potential staff</li>
              <li>Applications will appear in the Staff Applicants tab</li>
            </ol>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📋 Duplicate Event</h3>
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-gray-700">
              Click <strong>"Duplicate"</strong> on any event to create a copy. Useful for recurring conventions—just update the dates!
            </p>
          </div>
        </section>

        {/* ==================== COMMISSIONS ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            🎨 Commissions Tab
          </h2>

          <p className="text-gray-700 mb-6">
            Manage custom art requests from customers. Track each commission from initial request through completion and payment.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📥 How Commissions Come In</h3>
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="text-gray-700">
              Customers submit requests through the public commission form. New requests appear with <strong>"Pending"</strong> status.
            </p>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📋 Commission Statuses</h3>
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-yellow-100 p-3 rounded"><strong>Pending:</strong> New request, needs review</div>
            <div className="bg-blue-100 p-3 rounded"><strong>Needs Info:</strong> Waiting for customer details</div>
            <div className="bg-green-100 p-3 rounded"><strong>Approved:</strong> Quoted and ready for payment</div>
            <div className="bg-purple-100 p-3 rounded"><strong>In Progress:</strong> Work has begun</div>
            <div className="bg-gray-100 p-3 rounded"><strong>Completed:</strong> Finished and delivered</div>
            <div className="bg-red-100 p-3 rounded"><strong>Rejected:</strong> Declined request</div>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">✅ Processing a Commission</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 bg-green-50 p-4 rounded-lg mb-6">
            <li>Click on a commission to open its detail page</li>
            <li>Review the customer's request, references, and requirements</li>
            <li>If you need more info, click <strong>"Request Info"</strong> and type your question</li>
            <li>To approve, click <strong>"Approve"</strong> and enter your price quote</li>
            <li>The customer will receive notification with payment instructions</li>
            <li>Update payment status when payment is received (Zelle, CashApp, PayPal)</li>
            <li>Change status to <strong>"In Progress"</strong> when you start working</li>
            <li>Mark as <strong>"Completed"</strong> when finished</li>
          </ol>

          <h3 className="text-xl font-bold text-gray-800 mb-4">💬 In-App Messaging</h3>
          <div className="bg-pink-50 p-4 rounded-lg mb-6">
            <p className="text-gray-700 mb-2">
              Each commission has a message thread for communicating with the customer:
            </p>
            <ul className="space-y-1 text-gray-700">
              <li>• Type a message and click <strong>"Send"</strong></li>
              <li>• Customer can reply through their status page</li>
              <li>• When they reply, you'll see a <strong>"NEW MESSAGE"</strong> badge</li>
              <li>• Check the Commissions sidebar for the notification count</li>
            </ul>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">🔗 Sharing Commission Status Link</h3>
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <p className="text-gray-700">
              Click <strong>"Copy Status Link"</strong> to get a shareable URL. Send this to the customer so they can check their commission status and send messages without needing to log in.
            </p>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">🧾 Generating Invoices</h3>
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-gray-700 mb-2">
              Create professional invoices for commission payments:
            </p>
            <ol className="list-decimal list-inside space-y-1 text-gray-700">
              <li>Open the commission detail page</li>
              <li>Click <strong>"Generate Invoice"</strong> button</li>
              <li>Enter the invoice amount and any notes</li>
              <li>Click <strong>"Generate Invoice"</strong> to create</li>
              <li>Copy the invoice link to send to the customer</li>
              <li>Customer can view payment methods (Zelle, Apple Pay, Cash App)</li>
            </ol>
          </div>
        </section>

        {/* ==================== INVOICES ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            🧾 Invoices Tab
          </h2>

          <p className="text-gray-700 mb-6">
            Track and manage all commission invoices in one place. See unpaid balances, mark payments received, and copy invoice links.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📊 Invoice Dashboard</h3>
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <ul className="space-y-2 text-gray-700">
              <li><strong>Total Invoices:</strong> Count of all invoices generated</li>
              <li><strong>Unpaid:</strong> Outstanding balance waiting for payment (shown in yellow)</li>
              <li><strong>Collected:</strong> Total amount received (shown in green)</li>
            </ul>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📋 Managing Invoices</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 bg-green-50 p-4 rounded-lg mb-6">
            <li>Use the search bar to find invoices by customer name or invoice number</li>
            <li>Filter by status: All, Unpaid, or Paid</li>
            <li>Click <strong>"Copy Link"</strong> to share an invoice with a customer</li>
            <li>Click <strong>"View Commission"</strong> to see the related commission</li>
          </ol>

          <h3 className="text-xl font-bold text-gray-800 mb-4">💳 Marking as Paid</h3>
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <p className="text-gray-700 mb-2">When you receive payment, click the appropriate button:</p>
            <ul className="space-y-1 text-gray-700">
              <li>• <strong>Zelle:</strong> Blue button - payment via Zelle (516-424-4367)</li>
              <li>• <strong>Apple:</strong> Gray button - payment via Apple Pay (516-424-4367)</li>
              <li>• <strong>Cash App:</strong> Green button - payment via Cash App ($WallaceOz)</li>
            </ul>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📱 Payment Methods Reference</h3>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <ul className="space-y-1 text-gray-700">
              <li>💳 <strong>Zelle:</strong> 516-424-4367</li>
              <li>🍎 <strong>Apple Pay:</strong> 516-424-4367</li>
              <li>💵 <strong>Cash App:</strong> $WallaceOz</li>
            </ul>
          </div>
        </section>

        {/* ==================== CONVENTION SALES ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            🛒 Convention Sales Tab
          </h2>

          <p className="text-gray-700 mb-6">
            Create orders for in-person sales at conventions when customers want items shipped later. Orders sync directly to your Wix store!
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">🎯 When To Use Convention Sales</h3>
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="text-gray-700">Use this feature when a customer at your booth wants to:</p>
            <ul className="mt-2 space-y-1 text-gray-700">
              <li>• Purchase an item that's <strong>out of stock</strong> at the booth</li>
              <li>• Order a product in a <strong>different size</strong> than you have</li>
              <li>• Buy something and have it <strong>shipped home</strong> instead of carrying it</li>
            </ul>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">➕ Creating a New Order</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 bg-green-50 p-4 rounded-lg mb-6">
            <li>Select the <strong>Wix Store</strong>: LSD Collections (art) or Vyrus Clothing (shirts)</li>
            <li>Select the <strong>Convention/Event</strong> you're at</li>
            <li>Enter <strong>Customer Information</strong>: name, email, phone</li>
            <li>Enter <strong>Shipping Address</strong>: street, city, state, ZIP</li>
            <li>Add <strong>Products</strong>: Click the search icon to pick from catalog or quick select</li>
            <li>For quick select items (Canvas, Poster), enter the <strong>Design Name</strong></li>
            <li>Enter the <strong>Price</strong> (convention pricing)</li>
            <li>Click <strong>"Create Order & Sync to Wix"</strong></li>
          </ol>

          <h3 className="text-xl font-bold text-gray-800 mb-4">🔍 Selecting Products</h3>
          <div className="space-y-3 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-bold text-blue-700">Quick Select (No Design)</h4>
              <p className="text-sm text-gray-600 mb-2">Use for generic items when you'll specify the design:</p>
              <ul className="text-sm text-gray-600">
                <li>• <strong>Poster</strong> - Any size poster</li>
                <li>• <strong>Canvas</strong> - Choose size: 24x18, 32x24, 40x30, or 48x36</li>
                <li>• <strong>Acrylic Print</strong> - Standard acrylic</li>
                <li>• <strong>Vyrus Shirt</strong> - Choose size: XS to 5XL</li>
                <li>• <strong>Frame for Acrylic</strong> - Frame only</li>
                <li>• <strong>Trading Card</strong> - Individual cards</li>
              </ul>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="font-bold text-purple-700">From Wix Catalog (With Design)</h4>
              <p className="text-sm text-gray-600">
                Select specific products from your Wix store. For canvases and shirts, you'll be prompted to choose a size.
              </p>
            </div>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📦 Order History</h3>
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="text-gray-700 mb-2">Click the <strong>"Order History"</strong> tab to view all convention sales:</p>
            <ul className="space-y-1 text-gray-700">
              <li>• Filter by event to see sales from a specific convention</li>
              <li>• Track order status: <strong>Pending Shipment</strong> → <strong>Shipped</strong> → <strong>Delivered</strong></li>
              <li>• Click <strong>"Mark Shipped"</strong> when you mail the order</li>
              <li>• Click <strong>"Mark Delivered"</strong> when customer receives it</li>
            </ul>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">⚠️ Important Notes</h3>
          <div className="bg-red-50 p-4 rounded-lg">
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Prices are manual:</strong> Enter convention prices, not online prices</li>
              <li>• <strong>Payment is collected at booth:</strong> Orders are marked as paid in Wix</li>
              <li>• <strong>Design name is required:</strong> For quick select canvas/poster/acrylic items</li>
              <li>• <strong>Orders sync to Wix:</strong> They'll appear in your Wix Orders dashboard</li>
            </ul>
          </div>
        </section>

        {/* ==================== STAFF APPLICANTS ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            👥 Staff Applicants Tab
          </h2>

          <p className="text-gray-700 mb-6">
            Review and manage applications from people who want to work your events.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📋 Application Types</h3>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-bold text-green-700">Volunteer</h4>
              <p className="text-sm text-gray-600">Unpaid helpers who assist at the booth</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-bold text-blue-700">Paid Staff</h4>
              <p className="text-sm text-gray-600">Compensated workers for longer shifts</p>
            </div>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">✅ Reviewing Applications</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 bg-green-50 p-4 rounded-lg mb-6">
            <li>Click on an application to see full details</li>
            <li>Review their experience, availability, and responses</li>
            <li>Watch their video introduction if provided</li>
            <li>Check the auto-generated <strong>Score</strong> based on their answers</li>
            <li>Click <strong>"Accept"</strong> or <strong>"Reject"</strong></li>
            <li>Add notes for your records</li>
            <li>The applicant receives an automatic email notification</li>
          </ol>

          <h3 className="text-xl font-bold text-gray-800 mb-4">🔍 Filtering Applications</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700">Use the filters at the top to sort by:</p>
            <ul className="mt-2 space-y-1 text-gray-700">
              <li>• <strong>Event:</strong> See applications for a specific convention</li>
              <li>• <strong>Status:</strong> Pending, Accepted, or Rejected</li>
              <li>• <strong>Type:</strong> Volunteer or Paid</li>
            </ul>
          </div>
        </section>

        {/* ==================== TASK MANAGER ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            ✅ Task Manager Tab
          </h2>

          <p className="text-gray-700 mb-6">
            Assign and track tasks for the management team. Keep everyone aligned on what needs to get done.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">➕ Creating a Task</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 bg-green-50 p-4 rounded-lg mb-6">
            <li>Click <strong>"+ Add Task"</strong></li>
            <li>Enter a clear task title</li>
            <li>Add a description with details (optional)</li>
            <li>Assign to: <strong>Renee</strong>, <strong>Biz</strong>, or <strong>Ozzy</strong></li>
            <li>Set a deadline date</li>
            <li>Choose priority: <strong>Low</strong>, <strong>Medium</strong>, or <strong>High</strong></li>
            <li>Click <strong>"Create"</strong></li>
          </ol>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📋 Task Statuses</h3>
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-yellow-100 p-3 rounded text-center"><strong>Pending</strong><br/><span className="text-sm">Not started</span></div>
            <div className="bg-blue-100 p-3 rounded text-center"><strong>In Progress</strong><br/><span className="text-sm">Being worked on</span></div>
            <div className="bg-green-100 p-3 rounded text-center"><strong>Completed</strong><br/><span className="text-sm">Done!</span></div>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">🔍 Managing Tasks</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Filter by person:</strong> See only one team member's tasks</li>
              <li>• <strong>Filter by status:</strong> View pending, in progress, or completed</li>
              <li>• <strong>Update status:</strong> Click the status dropdown to change it</li>
              <li>• <strong>Edit task:</strong> Click to modify title, deadline, or assignee</li>
              <li>• <strong>Delete task:</strong> Remove tasks that are no longer needed</li>
            </ul>
          </div>

          <div className="bg-red-50 p-4 rounded-lg mt-6">
            <h4 className="font-bold text-red-700 mb-2">⚠️ Overdue Tasks</h4>
            <p className="text-gray-700">
              Tasks past their deadline will be highlighted and appear in Dashboard alerts. 
              Check these regularly to ensure nothing falls through the cracks!
            </p>
          </div>
        </section>

        {/* ==================== CALENDAR ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            📅 Calendar Tab
          </h2>

          <p className="text-gray-700 mb-6">
            Visual calendar showing all your events. Great for planning and seeing what's coming up.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">👁️ Calendar Views</h3>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-bold text-gray-700">Month View</h4>
              <p className="text-sm text-gray-600">See the full month with events displayed on their dates</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-bold text-gray-700">List View</h4>
              <p className="text-sm text-gray-600">Chronological list of all upcoming events</p>
            </div>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📋 How To Use</h3>
          <ul className="space-y-2 text-gray-700 bg-blue-50 p-4 rounded-lg">
            <li>• Click <strong>"Month"</strong> or <strong>"List"</strong> to switch views</li>
            <li>• Use arrows to navigate between months</li>
            <li>• Click on any event to go to its detail page</li>
            <li>• Events are color-coded by status</li>
          </ul>
        </section>

        {/* ==================== ANALYTICS ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            📊 Analytics Tab
          </h2>

          <p className="text-gray-700 mb-6">
            Get a high-level view of your business performance with charts and summaries.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📈 What You'll See</h3>
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Total Revenue vs Expenses:</strong> Overall financial picture</li>
              <li>• <strong>Net Profit:</strong> Your bottom line across all events</li>
              <li>• <strong>Event Status Breakdown:</strong> How many approved, pending, etc.</li>
              <li>• <strong>Expense Categories:</strong> Where your money is going (travel, booth, supplies)</li>
            </ul>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-gray-700">
              <strong>Tip:</strong> Review analytics monthly to identify trends and make better decisions about which events to prioritize.
            </p>
          </div>
        </section>

        {/* ==================== COMPARE ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            ⚖️ Compare Tab
          </h2>

          <p className="text-gray-700 mb-6">
            Compare multiple events side-by-side to see which ones perform best.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📋 How To Compare Events</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 bg-green-50 p-4 rounded-lg mb-6">
            <li>Select 2 or more events from the dropdown</li>
            <li>Click <strong>"Compare"</strong></li>
            <li>View side-by-side metrics: revenue, expenses, profit, ROI</li>
            <li>See which events gave you the best return</li>
          </ol>

          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-gray-700">
              <strong>Use Case:</strong> Compare the same convention across different years, or compare similar-sized events to decide which to prioritize.
            </p>
          </div>
        </section>

        {/* ==================== OPTIMIZER ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            ⚡ Optimizer Tab
          </h2>

          <p className="text-gray-700 mb-6">
            Get AI-powered recommendations to maximize your convention ROI.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">🎯 What Optimizer Shows</h3>
          <div className="space-y-3 mb-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-bold text-green-700">✅ High ROI Events</h4>
              <p className="text-sm text-gray-600">Events with best return on investment—prioritize similar ones</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <h4 className="font-bold text-red-700">⚠️ Underperforming Events</h4>
              <p className="text-sm text-gray-600">Events with negative ROI—consider skipping or renegotiating</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-bold text-blue-700">🔄 Recommended Returns</h4>
              <p className="text-sm text-gray-600">Events you marked "would return to" in debriefs</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h4 className="font-bold text-yellow-700">💰 Expense Tips</h4>
              <p className="text-sm text-gray-600">Your biggest expense categories to look for savings</p>
            </div>
          </div>
        </section>

        {/* ==================== REPORTS ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            📈 Reports Tab
          </h2>

          <p className="text-gray-700 mb-6">
            Generate financial summaries to share with partners or for your records.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📋 How To Generate a Report</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 bg-green-50 p-4 rounded-lg mb-6">
            <li>Select report type: Monthly, Quarterly, or Yearly</li>
            <li>Choose date range if needed</li>
            <li>Click <strong>"Generate Report"</strong></li>
            <li>View the summary with all financial data</li>
            <li>Optionally enter email addresses to send the report</li>
          </ol>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📊 Report Contents</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <ul className="space-y-1 text-gray-700">
              <li>• Total events in the period</li>
              <li>• Total revenue and expenses</li>
              <li>• Net profit and profit margin</li>
              <li>• Top performing events</li>
              <li>• Status breakdown</li>
            </ul>
          </div>
        </section>

        {/* ==================== DOCUMENTS ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            📁 Documents Tab
          </h2>

          <p className="text-gray-700 mb-6">
            Store and organize important company files and training materials.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">➕ Uploading a Document</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 bg-green-50 p-4 rounded-lg mb-6">
            <li>Click <strong>"Upload Document"</strong></li>
            <li>Enter a title for the document</li>
            <li>Select a category (Training, Policies, Templates, General)</li>
            <li>Paste the URL to the file (Google Drive, Dropbox, etc.)</li>
            <li>Check <strong>"Management Only"</strong> if it should be hidden from employees</li>
            <li>Click <strong>"Upload"</strong></li>
          </ol>

          <h3 className="text-xl font-bold text-gray-800 mb-4">📂 Document Categories</h3>
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-gray-50 p-3 rounded"><strong>Training:</strong> Guides, how-tos, onboarding</div>
            <div className="bg-gray-50 p-3 rounded"><strong>Policies:</strong> Rules, procedures, standards</div>
            <div className="bg-gray-50 p-3 rounded"><strong>Templates:</strong> Reusable forms and documents</div>
            <div className="bg-gray-50 p-3 rounded"><strong>General:</strong> Everything else</div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-bold text-yellow-700 mb-2">🔒 Management Only Documents</h4>
            <p className="text-gray-700">
              Check this box when uploading sensitive documents. These will only be visible to users with the Management role, not to Staff/Employees.
            </p>
          </div>
        </section>

        {/* ==================== USERS ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            👤 Users Tab
          </h2>

          <p className="text-gray-700 mb-6">
            Manage team member accounts and approve new user registrations.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">👥 User Roles Explained</h3>
          <div className="space-y-3 mb-6">
            <div className="bg-pink-50 p-4 rounded-lg">
              <h4 className="font-bold text-pink-700">Management</h4>
              <p className="text-sm text-gray-600">Full access: financials, user management, all features</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-bold text-blue-700">Staff</h4>
              <p className="text-sm text-gray-600">Limited access: can see events and calendar, no financial data</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h4 className="font-bold text-yellow-700">Pending</h4>
              <p className="text-sm text-gray-600">New registrations waiting for approval—cannot log in yet</p>
            </div>
          </div>

          <h3 className="text-xl font-bold text-gray-800 mb-4">✅ Approving New Users</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 bg-green-50 p-4 rounded-lg mb-6">
            <li>New employees register at the <strong>/register</strong> page</li>
            <li>Their account starts with "Pending" status</li>
            <li>You'll see them in the Dashboard alerts and Users tab</li>
            <li>Click <strong>"Approve"</strong> to give them Staff access</li>
            <li>Or click <strong>"Reject"</strong> to deny the request</li>
            <li>Approved users receive an email notification</li>
          </ol>
        </section>

        {/* ==================== CONVENTION DATABASE ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            🔍 Find Conventions Tab
          </h2>

          <p className="text-gray-700 mb-6">
            Search a database of conventions to find new events to apply to.
          </p>

          <h3 className="text-xl font-bold text-gray-800 mb-4">➕ Adding a Convention</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 bg-green-50 p-4 rounded-lg mb-6">
            <li>Click <strong>"+ Add Convention"</strong></li>
            <li>Enter convention name and location</li>
            <li>Add typical dates (e.g., "First weekend of March")</li>
            <li>Enter estimated attendance and booth costs</li>
            <li>Add website URL and notes</li>
            <li>Save to your database</li>
          </ol>

          <h3 className="text-xl font-bold text-gray-800 mb-4">🔍 Searching</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <ul className="space-y-1 text-gray-700">
              <li>• Search by name, city, or state</li>
              <li>• Filter by convention type (anime, comic, gaming, etc.)</li>
              <li>• View estimated booth costs to plan budget</li>
            </ul>
          </div>
        </section>

        {/* ==================== QUICK REFERENCE ==================== */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-pink-500 mb-6 pb-2 border-b-2 border-pink-200">
            ⚡ Quick Reference
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-3">🔑 Login URL</h3>
              <p className="text-sm text-gray-600 break-all">https://vyrus-hub-exec.preview.emergentagent.com</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-3">📱 Add to Phone</h3>
              <p className="text-sm text-gray-600">Safari/Chrome → Share → Add to Home Screen</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-3">💳 Payment Methods</h3>
              <p className="text-sm text-gray-600">Zelle, CashApp, PayPal</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-bold text-gray-800 mb-3">👥 Team Members</h3>
              <p className="text-sm text-gray-600">Renee, Biz, Ozzy</p>
            </div>
          </div>
        </section>

        {/* ==================== FOOTER ==================== */}
        <div className="text-center pt-8 mt-8 border-t-4 border-pink-500">
          <p className="text-xl text-pink-500 font-bold mb-2">VYRUS HUB</p>
          <p className="text-gray-500">For questions or support, contact the team!</p>
          <p className="text-pink-500 font-bold mt-4">— The Vyrus Team 💖</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden { display: none !important; }
          section { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}
